import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile } from '../../services/fileUploadService';
import olreadiaService from '../../services/olreadiaService';
import { getUser } from '../../services/authService';
import { useBusinessLocationTypes } from '../../hooks/useBusinessLocationTypes';
import { useAppConfig } from '../../hooks/useAppConfig';
import GooglePlacesAutocompleteInput from '../../components/common/GooglePlacesAutocompleteInput';
import { addressFieldsFromPlaceDetails } from '../../utils/googlePlaces';
import './BusinessRegistration.css';

interface LocationType {
  id: string;
  name?: string;
  label?: string;
  code?: string;
  description?: string;
}

type UploadedBusinessFile = {
  fileId: string;
  name: string;
  previewUrl?: string;
  uploadedAt: string;
};

const getStoredActorId = () => {
  return getUser()?.id;
};

const BusinessRegistration = () => {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const { locationTypes, loading: isLoadingTypes, error: locationTypesError, fetchLocationTypes } =
    useBusinessLocationTypes();
  const { googleMapsApiKey } = useAppConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [addressSearch, setAddressSearch] = useState('');
  
  const [certificateFileId, setCertificateFileId] = useState<string | null>(null);
  const [logoFileId, setLogoFileId] = useState<string | null>(null);
  const [profilePhotoFileId, setProfilePhotoFileId] = useState<string | null>(null);
  const [otherDocFileIds, setOtherDocFileIds] = useState<string[]>([]);
  const [certificateFile, setCertificateFile] = useState<UploadedBusinessFile | null>(null);
  const [logoFile, setLogoFile] = useState<UploadedBusinessFile | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<UploadedBusinessFile | null>(null);
  const [otherDocFiles, setOtherDocFiles] = useState<UploadedBusinessFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    ein: '',
    businessLocationTypeId: '',
    description: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    website: '',
    contactPersonName: '',
    contactPersonTitle: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    county: '',
    country: 'US',
    latitude: '',
    longitude: '',
    placeId: '',
  });

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/signin?redirect=business');
      return;
    }
    fetchLocationTypes({ size: 100 });
  }, [isLoggedIn, navigate, fetchLocationTypes]);

  useEffect(() => {
    setError(locationTypesError);
  }, [locationTypesError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressSelected = ({ details }: { details: any }) => {
    const next = addressFieldsFromPlaceDetails(details);
    setFormData((prev) => ({
      ...prev,
      addressLine1: next.addressLine1,
      city: next.city,
      state: next.state,
      zipCode: next.zipCode,
      county: next.county,
      country: next.country || prev.country,
      latitude: next.latitude !== undefined ? String(next.latitude) : '',
      longitude: next.longitude !== undefined ? String(next.longitude) : '',
      placeId: next.placeId,
    }));
  };

  const uploadedFileRef = (file: File, fileId: string): UploadedBusinessFile => ({
    fileId,
    name: file.name,
    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    uploadedAt: new Date().toLocaleString(),
  });

  const renderBusinessFile = (
    label: string,
    file: UploadedBusinessFile | null,
    onRemove: () => void
  ) => {
    if (!file) return null;
    return (
      <div className="business-file-card">
        <div className="business-file-preview">
          {file.previewUrl ? <img src={file.previewUrl} alt={label} /> : <span>File</span>}
        </div>
        <div className="business-file-meta">
          <strong>{label}</strong>
          <span>{file.name}</span>
          <small>Uploaded {file.uploadedAt}</small>
          <small>ID: {file.fileId.slice(0, 8)}...</small>
        </div>
        <button type="button" onClick={onRemove}>Remove</button>
      </div>
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const files = e.target.files;
    const actorId = user?.id || getStoredActorId();
    if (!files || !actorId) {
      setMessage({ type: 'error', text: 'You must be logged in to upload files' });
      return;
    }
    
    setUploadingFiles(true);
    setMessage(null);
    
    try {
      switch (type) {
        case 'certificate': {
          const result = await uploadFile(actorId, files[0], { 
            purpose: 'OTHER',
            fileType: 'DOCUMENT',
            confirm: false,
          });
          setCertificateFileId(result.fileId);
          setCertificateFile(uploadedFileRef(files[0], result.fileId));
          setMessage({ type: 'success', text: 'Certificate uploaded successfully' });
          break;
        }
        case 'logo': {
          const result = await uploadFile(actorId, files[0], { 
            purpose: 'OTHER',
            fileType: 'PHOTO',
            confirm: false,
          });
          setLogoFileId(result.fileId);
          setLogoFile(uploadedFileRef(files[0], result.fileId));
          setMessage({ type: 'success', text: 'Logo uploaded successfully' });
          break;
        }
        case 'profilePhoto': {
          const result = await uploadFile(actorId, files[0], { 
            purpose: 'OTHER',
            fileType: 'PHOTO',
            confirm: false,
          });
          setProfilePhotoFileId(result.fileId);
          setProfilePhotoFile(uploadedFileRef(files[0], result.fileId));
          setMessage({ type: 'success', text: 'Profile photo uploaded successfully' });
          break;
        }
        case 'otherDocs': {
          const uploadedIds: string[] = [];
          const uploadedFiles: UploadedBusinessFile[] = [];
          for (let i = 0; i < files.length; i++) {
            const result = await uploadFile(actorId, files[i], { 
              purpose: 'OTHER',
              fileType: 'DOCUMENT',
              confirm: false,
            });
            uploadedIds.push(result.fileId);
            uploadedFiles.push(uploadedFileRef(files[i], result.fileId));
          }
          setOtherDocFileIds([...otherDocFileIds, ...uploadedIds]);
          setOtherDocFiles([...otherDocFiles, ...uploadedFiles]);
          setMessage({ type: 'success', text: `${uploadedIds.length} document(s) uploaded successfully` });
          break;
        }
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setMessage({ type: 'error', text: err?.message || 'Failed to upload file' });
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Business name is required' });
      return;
    }
    if (!formData.businessLocationTypeId) {
      setMessage({ type: 'error', text: 'Please select a business location type' });
      return;
    }
    if (!formData.addressLine1.trim() || !formData.city.trim() || !formData.state.trim()) {
      setMessage({ type: 'error', text: 'Address line 1, city, and state are required' });
      return;
    }
    
    setIsLoading(true);
    setMessage(null);

    try {
      const actorId = user?.id || getStoredActorId();
      if (!actorId) {
        setMessage({ type: 'error', text: 'Your profile is still loading. Please refresh and try again.' });
        setIsLoading(false);
        return;
      }
      const payload: any = {
        name: formData.name,
        status: 'PENDING_REVIEW',
        businessLocationTypeId: formData.businessLocationTypeId,
      };
      
      if (formData.ein && formData.ein.trim()) payload.ein = formData.ein;
      if (formData.description && formData.description.trim()) payload.description = formData.description;
      
      if (certificateFileId) payload.certificate = certificateFileId;
      if (logoFileId) payload.logo = logoFileId;
      if (profilePhotoFileId) payload.profilePhoto = profilePhotoFileId;
      if (otherDocFileIds.length > 0) payload.otherDocs = otherDocFileIds;
      
      const hasContactInfo = formData.primaryEmail || formData.primaryPhone || formData.website ||
                             formData.secondaryEmail || formData.secondaryPhone;
      
      if (hasContactInfo) {
        payload.contactInformation = {};
        if (formData.primaryEmail) payload.contactInformation.primaryEmail = formData.primaryEmail;
        if (formData.secondaryEmail) payload.contactInformation.secondaryEmail = formData.secondaryEmail;
        if (formData.primaryPhone) payload.contactInformation.primaryPhone = formData.primaryPhone;
        if (formData.secondaryPhone) payload.contactInformation.secondaryPhone = formData.secondaryPhone;
        if (formData.website) payload.contactInformation.websiteUrl = formData.website;
        if (formData.contactPersonName) payload.contactInformation.contactPersonName = formData.contactPersonName;
        if (formData.contactPersonTitle) payload.contactInformation.contactPersonTitle = formData.contactPersonTitle;
      }
      
      const hasLocation = formData.addressLine1 || formData.city || formData.state;
      if (hasLocation) {
        payload.locationInformation = {
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          county: formData.county,
          country: formData.country,
        };
        
        const latitude = Number(formData.latitude);
        const longitude = Number(formData.longitude);
        if (Number.isFinite(latitude)) payload.locationInformation.latitude = latitude;
        if (Number.isFinite(longitude)) payload.locationInformation.longitude = longitude;
        if (formData.placeId) payload.locationInformation.placeId = formData.placeId;
      }

      console.log('🚀 Sending business creation payload:', JSON.stringify(payload, null, 2));
      console.log('👤 Actor ID:', actorId);
      
      const business = await olreadiaService.businessEntities.create(payload, actorId);
      console.log('✅ Business creation response:', business);
      console.log('🔑 Business ID:', business?.id);
      
      if (!business || !business.id) {
        console.error('❌ No business ID in response. Full response:', business);
        setMessage({ type: 'error', text: 'Business was created but the response did not contain an ID. Please check the backend logs.' });
        setIsLoading(false);
        return;
      }
      
      console.log(`🎉 Business created successfully with ID: ${business.id}. Redirecting to business landing page...`);
      setMessage({ type: 'success', text: 'Business registered successfully! Redirecting...' });
      navigate('/account/business', { replace: true });
    } catch (err: any) {
      console.error('❌ Registration error:', err);
      console.error('Error details:', err?.response?.data || err?.message);
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to register business. Please try again.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="business-registration-container">
      <div className="registration-header">
        <h1>Business Entity Registration</h1>
        <p>Register a business profile using the same profile service flow as the portal.</p>
      </div>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
          <button onClick={() => fetchLocationTypes({ size: 100 })}>Retry</button>
        </div>
      )}

      {message && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="registration-form">
        {/* Business Details Section */}
        <div className="form-section">
          <h2>Business Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Business Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Business name"
                required
              />
            </div>
            <div className="form-group">
              <label>EIN</label>
              <input
                type="text"
                name="ein"
                value={formData.ein}
                onChange={handleInputChange}
                placeholder="Employer Identification Number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Business Location Type *</label>
              {isLoadingTypes ? (
                <div className="loading-types">Loading location types...</div>
              ) : (
                <select 
                  name="businessLocationTypeId" 
                  value={formData.businessLocationTypeId} 
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select location type</option>
                  {(locationTypes as LocationType[]).map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label || type.name || type.code} {type.code ? `(${type.code})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Short description of your business"
              rows={3}
            />
          </div>
        </div>

        {/* File Uploads Section */}
        <div className="form-section">
          <h2>Files</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Certificate</label>
              <input type="file" accept="image/*,.pdf" onChange={(e) => handleFileChange(e, 'certificate')} disabled={uploadingFiles} />
              {renderBusinessFile('Certificate', certificateFile, () => {
                setCertificateFileId(null);
                setCertificateFile(null);
              })}
            </div>
            <div className="form-group">
              <label>Logo</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'logo')} disabled={uploadingFiles} />
              {renderBusinessFile('Logo', logoFile, () => {
                setLogoFileId(null);
                setLogoFile(null);
              })}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Profile Photo</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'profilePhoto')} disabled={uploadingFiles} />
              {renderBusinessFile('Profile Photo', profilePhotoFile, () => {
                setProfilePhotoFileId(null);
                setProfilePhotoFile(null);
              })}
            </div>
            <div className="form-group">
              <label>Other Docs</label>
              <input type="file" multiple accept="image/*,.pdf,.docx" onChange={(e) => handleFileChange(e, 'otherDocs')} disabled={uploadingFiles} />
              {otherDocFiles.length > 0 && (
                <div className="business-file-list">
                  {otherDocFiles.map((file, index) => (
                    <div key={file.fileId}>
                      {renderBusinessFile(`Document ${index + 1}`, file, () => {
                        setOtherDocFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
                        setOtherDocFileIds((prev) => prev.filter((id) => id !== file.fileId));
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="form-section">
          <h2>Contact Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Primary Email</label>
              <input
                type="email"
                name="primaryEmail"
                value={formData.primaryEmail}
                onChange={handleInputChange}
                placeholder="email@domain.com"
              />
            </div>
            <div className="form-group">
              <label>Secondary Email</label>
              <input
                type="email"
                name="secondaryEmail"
                value={formData.secondaryEmail}
                onChange={handleInputChange}
                placeholder="secondary@domain.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Primary Phone</label>
              <input
                type="tel"
                name="primaryPhone"
                value={formData.primaryPhone}
                onChange={handleInputChange}
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="form-group">
              <label>Secondary Phone</label>
              <input
                type="tel"
                name="secondaryPhone"
                value={formData.secondaryPhone}
                onChange={handleInputChange}
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://www.business.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Person Name</label>
              <input
                type="text"
                name="contactPersonName"
                value={formData.contactPersonName}
                onChange={handleInputChange}
                placeholder="Full name"
              />
            </div>
            <div className="form-group">
              <label>Contact Person Title</label>
              <input
                type="text"
                name="contactPersonTitle"
                value={formData.contactPersonTitle}
                onChange={handleInputChange}
                placeholder="Owner / Manager"
              />
            </div>
          </div>
        </div>

        {/* Location Information Section */}
        <div className="form-section">
          <h2>Location Information</h2>
          <p className="section-hint">Search and select an address to auto-fill location fields.</p>
          
          <div className="form-group">
            <label>Search for an address</label>
            <GooglePlacesAutocompleteInput
              googleMapsApiKey={googleMapsApiKey}
              placeholder="Search for an address..."
              value={addressSearch}
              onChange={setAddressSearch}
              onResolvePlace={handleAddressSelected}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                placeholder="Address Line 1"
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                placeholder="Address Line 2"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="State"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                placeholder="Zip Code"
              />
            </div>
            <div className="form-group">
              <label>County</label>
              <input
                type="text"
                name="county"
                value={formData.county}
                onChange={handleInputChange}
                placeholder="County"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <select name="country" value={formData.country} onChange={handleInputChange}>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="UK">United Kingdom</option>
                <option value="AU">Australia</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Latitude</label>
              <input
                type="text"
                name="latitude"
                value={formData.latitude || ''}
                onChange={handleInputChange}
                placeholder="e.g. 47.6062"
              />
            </div>
            <div className="form-group">
              <label>Longitude</label>
              <input
                type="text"
                name="longitude"
                value={formData.longitude || ''}
                onChange={handleInputChange}
                placeholder="e.g. -122.3321"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Place ID</label>
            <input
              type="text"
              name="placeId"
              value={formData.placeId || ''}
              onChange={handleInputChange}
              placeholder="Google Place ID"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate('/')}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isLoading || isLoadingTypes || uploadingFiles}>
            {uploadingFiles ? 'Uploading files...' : isLoading ? 'Registering...' : 'Register Business'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessRegistration;
